import { Component, HostListener, Input, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { BenchmarkDashboardDataService } from '../benchmark-dashboard.data.service';
import { BenchmarkDashboardDatapoint, EngergyDashboardData, TimeRange } from '../benchmark-dashboard.data.interface';
import { ColorService, NumberFormatService, TimeDateConverterService, UnitFormatService } from '@converter';
import { LoaderService } from '@loader';
import { DataPointsService } from '@datapoints';
import { PlantService } from '@plants';
import { NumberFormattingOption } from '@localisation';
import { AxisHelperService } from '../axis-helper.service';
import { DxChartComponent } from 'devextreme-angular';


interface ChartSeries {
    key : string,
    name : string,
    valueField : string,
    argumentField : string,
    axis? : string,
}


@Component({
    selector: 'benchmark-bar-chart',
    templateUrl: './benchmark.barchart.template.html',
    standalone: false
})
export class EngeryBarChartComponent {
    constructor(
        private service: BenchmarkDashboardDataService,
        private colorConverter: ColorService,
        private loaderService: LoaderService,
        private datapointService: DataPointsService,
        private plantService: PlantService,
        private dateService: TimeDateConverterService,
        private unitFormatService: UnitFormatService,
        private axesHelperService: AxisHelperService,
        public numberFormatService: NumberFormatService
    ){
        this.argumentLabelFormaterBound = this.argumentLabelFormater.bind(this);
    }

    public cssWidth: string = 'auto'

    public values: any[] = [];

    public series : ChartSeries[] = [];

    public hasData: boolean = false;

    public viewTitle: string = '';

    public axes: any[] = [];

    @ViewChild('container')
    public container: ElementRef;

    
    @ViewChild('chart')
    public chart: DxChartComponent;

    @Input()
    public chartIndex: number = 0;

    @Input()
    public formatNumberAsFloat: boolean = false;

    @Input()
    public showSeriesLabel: boolean;

    @Input()
    public width: 'fixed' | 'auto' = 'auto'

    @Output()
    public dropped: EventEmitter<any> = new EventEmitter()

    @HostListener('drop', ['$event'])
    public onDrop(e: any){
        this.dropped.emit(e);
    }

    @HostListener('dragover', ['$event'])
    public onDragOver(e: any){
        e.dataTransfer.dropEffect = 'move';
        return false;
    }

    private resizeTimer: any;

    @HostListener('window:resize')
    public onResize(){
        this.hasData = false;
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            this.hasData = true;
        }, 100)
     
    }

    private async getDatapointTitle(title: string, plantId: string, datapointId: string) {
        if(plantId && datapointId){
            try {
                const data = await this.datapointService.getDataPointInfo(plantId + ';' + datapointId);
                title = this.convertTokenByData(title, data);

                const converted = await this.plantService.convertSiteInfoTokens(title, plantId);
                return converted.replace(/\[(.*?)\]/gmi, (arg1, arg2, arg3) => {
                    return '';
                })
            }catch(e) {
                return 'title not found'
            }
        }
        return ''
    }

    private convertTokenByData(token: string, data: any){
        const precentReplacements = [
            { char: '%t', prop: 'objectName' },
            { char: '%N', prop: 'memberName' },
            { char: '%u', prop: 'descr' },
            { char: '%T', prop: 'descriptionName' }
        ];

        precentReplacements.forEach((current) => {
            if (token) {
                token = token.replace(new RegExp(current.char, 'gm'), data[current.prop] || '');
            }
        });
        return token;
    }

    private axisCache = {};


    private setWidth(groupLength, datapointLength) {
        if(this.width === 'fixed') {
            const width = (groupLength * datapointLength * 70 * 2) + 250;
            this.cssWidth = width + 'px';
        }else{
            this.cssWidth = 'auto'
        }
    }

    public async update(currentData: EngergyDashboardData) {
        this.seriesLabelFormaters = {};
        
        this.labelCache = {};
        this.axisCache = {};
        this.hasData = false;
        const spinnerRef = this.loaderService.start();
        this.loaderService.setText($localize`:@@benchmark-dashboard.chart.loadermessage: loading chart data...`)
        try {
            const result = await this.service.getData(
                currentData.charts[this.chartIndex].datapoints,
                currentData.from,
                currentData.to,
                currentData.valueAggregation,
                currentData.aggregationRange,
                currentData.compareRange
            );
            const win = window as any;
            win.benchmarkData = result;
            this.setWidth(result.length, currentData.charts[0].datapoints.length)
            if(result.length) {
                this.series = await this.createSeries(currentData.charts[this.chartIndex].datapoints, !!currentData.compareRange);
                this.axes = await this.axesHelperService.createAxisFromDatapoints(
                    currentData.charts[this.chartIndex].datapoints,
                    this.axisCache
                )
                this.values = result;
                this.hasData = true;
            }else{
                console.info('result is not defined')
            }
            
        }catch(e) {
            console.error('Error while updating chart!', e)
        }finally{
            this.loaderService.stop(spinnerRef);
        }
        
    }



    private async createSeries(datapoints: BenchmarkDashboardDatapoint[], hasCompareRange: boolean) {
        const series = []
        if(datapoints?.length){
            for(const datapoint of  datapoints) {
                const key = datapoint.plantId +  datapoint.datapointId;
                const datapointDetails = await this.datapointService.getDataPointDetails(
                    datapoint.plantId + ';' + datapoint.datapointId
                )
                if(this.formatNumberAsFloat) {
                    if(datapointDetails.rep === 2) {
                        datapointDetails.rep = 1;
                    }
                }
                this.datapointInfo[key] = datapointDetails;
                const datapointName = await this.getDatapointTitle(datapoint.name, datapoint.plantId, datapoint.datapointId);
                const axisName = this.axesHelperService.getAxisName(datapoint.plantId, datapoint.datapointId, this.datapointInfo[key] );
                series.push({
                    key,
                    name: datapointName,
                    valueField: datapoint.plantId + '_' + datapoint.datapointId,
                    axis: axisName,
                    color: datapoint.color
                })

                if(hasCompareRange) {
                    series.push({
                        key,
                        name: datapointName + ' ' + $localize`:@@benchmark-dashboard.chart.postfix.compare-value:compare value`,
                        valueField: datapoint.plantId + '_' + datapoint.datapointId + '_compare',
                        axis: axisName,
                        color: this.colorConverter.hexToRgba(datapoint.color, 0.7)
                    })
                }
                
            }
            return series;
        }
    }

    public argumentLabelFormaterBound: () => string;

    private getFormatedTimeRange(range: TimeRange, value: number) {
        value = +value;
        switch(range) {
            case TimeRange.day:
                return this.dateService.convertFormat(value, 'date')
            case TimeRange.month:
                return this.dateService.convertFormat(value, 'MMMM YYYY')
            case TimeRange.week:
                return $localize`:@@benchmark-dashboard.chart.label.week:week` + ' ' + this.dateService.getWeekNumberFromTimestamp(value)
            case TimeRange.year:
                return this.dateService.convertFormat(value, 'YYYY')
        }

    }


    private getArgumetLabel(aggregationRange: TimeRange, compareRange: TimeRange | 'null', value: number, compareValue){
        let output = this.getFormatedTimeRange(aggregationRange, value);
        if(compareRange && compareRange !== 'null') {
            if(compareValue === 'null') {
                output  += '\n' + '---'
            }else{
                output += '\n' + this.getFormatedTimeRange(
                    aggregationRange,
                    this.service.getCompareDate(Number(value), compareRange)
                );
            }
           
        }
        return output;
    }


    private labelCache: any = {}
    public argumentLabelFormater(value) {
        if(this.labelCache[value.value]) {
            return this.labelCache[value.value];
        }
        const splited = value.value.split(':')
        this.labelCache[value.value] = this.getArgumetLabel(splited[0], splited[1], splited[3], splited[4]);
        return this.labelCache[value.value];
    }
    
    private datapointInfo: any = {};

    private seriesLabelFormaters: any = {};

    public numberFormatingOptions : NumberFormattingOption;

    public createSeriesLabelFormater(series){
        if(!this.datapointInfo[series.key]){
            return (input) => {
                return input.valueText;
            }
        }
       
        if(this.seriesLabelFormaters[series.key]){
            return this.seriesLabelFormaters[series.key]
        }

        this.seriesLabelFormaters[series.key] = (input) => {
            const format = this.unitFormatService.format(
                input.value,
                this.datapointInfo[series.key].descr,
                this.datapointInfo[series.key].rep,
                true,
                null,
                this.numberFormatingOptions
            );
            return format.value + ' ' + format.unit;
        }
        return this.seriesLabelFormaters[series.key];
    }
    
    public customizeTooltipBound = this.customizeTooltip.bind(this);

    public customizeTooltip(data){
        if(data?.point?.data){
 
            if(!data?.point?.series?.getValueFields()?.[0]) {
                return '';
            }

            // @Info the only way to get the series key in a tooltip
            let result = data?.point?.series.getValueFields()[0];
            result = result.replace('_compare', '').replace('_', '');

            if(result && this.datapointInfo[result]) {
                const format = this.unitFormatService.format(
                    data.value,
                    this.datapointInfo[result].descr,
                    this.datapointInfo[result].rep,
                    true,
                    null,
                    this.numberFormatingOptions
                );
                data.valueText = data.seriesName + '<br>' + format.value + ' ' + format.unit;
            }else {
                data.valueText =  ''
            }
            
        
        }
        return data;
    }

    ngOnDestroy() {
        const win = window as any;
        delete win.benchmarkData
    }

}